import { useCallback, useEffect, useMemo, useState } from "react";
import useAuthStore from "../states/authStore";
import { NavBar, PageHeader, PageHeaderProvider, AppSwitcher } from "@iq/models";
import { TbLayoutDashboard } from "react-icons/tb";
import { FiAlertCircle, FiAlertOctagon, FiInbox, FiRefreshCw } from "react-icons/fi";
import { GoHome } from "react-icons/go";
import AppUserMenu from "../components/AppUserMenu";
import HeaderClockAction from "../components/HeaderClockAction";
import Dashboard from "./pages/Dashboard";
import Incidents from "./pages/Incidents";
import Problems from "./pages/Problems";
import Changes from "./pages/Changes";
import PortalRequests from "./pages/PortalRequests";
import IncidentDetail from "./pages/incidents/[id]";
import ProblemDetail from "./pages/problems/[id]";
import ChangeDetail from "./pages/changes/[id]";
import PortalRequestDetail from "./pages/portal-requests/[id]";
import AssignProblemModal from "./components/AssignProblemModal/AssignProblemModal";
import LinkChangeModal from "./components/LinkChangeModal/LinkChangeModal";
import LinkIncidentModal from "./components/LinkIncidentModal/LinkIncidentModal";
import LinkProblemModal from "./components/LinkProblemModal/LinkProblemModal";
import OrphanedTicketModal from "./components/OrphanedTicketModal/OrphanedTicketModal";
import { deleteChange, deleteProblem, deleteTicketRelation, getChanges, getClients, getIncidents, getProblems, updateChange, updateIncident, updateProblem } from "./services/ticket_service";
import { convertPortalRequest, getPortalRequests, updatePortalRequestStatus } from "./services/portal_request_service";
import { getTeamMembers } from "../CRM/services/meeting_service";
import useAppDataStore from "../states/useAppDataStore";
import CRMLogo from "../assets/icons/CRM_logo.svg";
import TicketsLogo from "../assets/icons/Tickets_logo.svg";
import AdminLogo from "../assets/icons/Admin_logo.svg";
import "./Main.css";
const itNavItems = [
    { id: "Dashboard", label: "Dashboard", icon: <TbLayoutDashboard size={24} /> },
    { id: "Portal Requests", label: "Portal Requests", icon: <FiInbox size={24} /> },
    { id: "Incidents", label: "Incidents", icon: <FiAlertCircle size={24} /> },
    { id: "Problems", label: "Problems", icon: <FiAlertOctagon size={24} /> },
    { id: "Changes", label: "Changes", icon: <FiRefreshCw size={24} /> },
];

const IT_PAGE_HEADER_TITLES = {
	Dashboard: "Dashboard",
    "Portal Requests": "Portal Requests",
	Incidents: "Incidents",
	Problems: "Problems",
	Changes: "Changes",
};

function IT_Main() {
    const appLinks = useMemo(() => ([
        { label: "CRM", image: CRMLogo, href: "/contacts", variant: "crm" },
        { label: "Tickets", image: TicketsLogo, href: "/IT", variant: "tickets" },
        { label: "Admin", image: AdminLogo, href: "/admin", variant: "admin" },
        { label: "Projects", image: AdminLogo, href: "/projects", variant: "projects" },
        { label: "Payment", image: AdminLogo, href: "/payment", variant: "payment" },
    ]), []);

    const [activePage, setActivePage] = useState("Dashboard");
    const [selectedPortalRequestId, setSelectedPortalRequestId] = useState(null);
    const [selectedPortalRequestPreview, setSelectedPortalRequestPreview] = useState(null);
    const [selectedIncidentId, setSelectedIncidentId] = useState(null);
    const [selectedIncidentPreview, setSelectedIncidentPreview] = useState(null);
    const [incidentDetailReturnPage, setIncidentDetailReturnPage] = useState("Incidents");
    const [selectedProblemId, setSelectedProblemId] = useState(null);
    const [selectedProblemPreview, setSelectedProblemPreview] = useState(null);
    const [problemDetailReturnPage, setProblemDetailReturnPage] = useState("Problems");
    const [selectedChangeId, setSelectedChangeId] = useState(null);
    const [selectedChangePreview, setSelectedChangePreview] = useState(null);
    const [changeDetailReturnPage, setChangeDetailReturnPage] = useState("Changes");
    const [incidents, setIncidents] = useState([]);
    const [clients, setClients] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [problems, setProblems] = useState([]);
    const [changes, setChanges] = useState([]);
    const [portalRequests, setPortalRequests] = useState([]);
    const [loadingIncidents, setLoadingIncidents] = useState(false);
    const [incidentsError, setIncidentsError] = useState("");
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientsError, setClientsError] = useState("");
    const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
    const [teamMembersError, setTeamMembersError] = useState("");
    const [loadingProblems, setLoadingProblems] = useState(false);
    const [problemsError, setProblemsError] = useState("");
    const [loadingChanges, setLoadingChanges] = useState(false);
    const [changesError, setChangesError] = useState("");
    const [loadingPortalRequests, setLoadingPortalRequests] = useState(false);
    const [portalRequestsError, setPortalRequestsError] = useState("");
    const [assigningByIncidentId, setAssigningByIncidentId] = useState({});
    const [isAssignProblemModalOpen, setIsAssignProblemModalOpen] = useState(false);
    const [isLinkChangeModalOpen, setIsLinkChangeModalOpen] = useState(false);
    const [isLinkIncidentModalOpen, setIsLinkIncidentModalOpen] = useState(false);
    const [isLinkProblemModalOpen, setIsLinkProblemModalOpen] = useState(false);
    const [orphanedTickets, setOrphanedTickets] = useState([]);
    const [orphanResolutionError, setOrphanResolutionError] = useState("");
    const [resolvingOrphan, setResolvingOrphan] = useState(false);
    const [relationRefreshVersion, setRelationRefreshVersion] = useState(0);

    const products = useAppDataStore((state) => state.products);
    const profile = useAuthStore((state) => state.profile);
    const profileName = profile?.name ?? "User";
    const profileId = profile?.id ?? null;
        const [pageHeader, setPageHeader] = useState({
		title: IT_PAGE_HEADER_TITLES.Dashboard,
		back: false,
		onBack: null,
		children: null,
	});

    const selectedIncident = useMemo(() => {
        if (!selectedIncidentId) {
            return null;
        }

        return (
            incidents.find((incident) => Number(incident?.id) === Number(selectedIncidentId)) ||
            selectedIncidentPreview ||
            null
        );
    }, [incidents, selectedIncidentId, selectedIncidentPreview]);

    const selectedPortalRequest = useMemo(() => {
        if (!selectedPortalRequestId) {
            return null;
        }
        return portalRequests.find((request) => Number(request?.id) === Number(selectedPortalRequestId)) || selectedPortalRequestPreview || null;
    }, [portalRequests, selectedPortalRequestId, selectedPortalRequestPreview]);

    const selectedProblem = useMemo(() => {
        if (!selectedProblemId) {
            return null;
        }

        return (
            problems.find((problem) => Number(problem?.id) === Number(selectedProblemId)) ||
            selectedProblemPreview ||
            null
        );
    }, [problems, selectedProblemId, selectedProblemPreview]);

    const selectedChange = useMemo(() => {
        if (!selectedChangeId) {
            return null;
        }

        return (
            changes.find((change) => Number(change?.id) === Number(selectedChangeId)) ||
            selectedChangePreview ||
            null
        );
    }, [changes, selectedChangeId, selectedChangePreview]);

    const loadIncidents = useCallback(async () => {
        setLoadingIncidents(true);
        setIncidentsError("");
        try {
            const data = await getIncidents();
            setIncidents(Array.isArray(data) ? data : []);
        } catch (error) {
            setIncidents([]);
            setIncidentsError(error?.message || "Failed to load incidents");
        } finally {
            setLoadingIncidents(false);
        }
    }, []);

    const loadClients = useCallback(async () => {
        setLoadingClients(true);
        setClientsError("");
        try {
            const data = await getClients();
            setClients(Array.isArray(data) ? data : []);
        } catch (error) {
            setClients([]);
            setClientsError(error?.message || "Failed to load clients");
        } finally {
            setLoadingClients(false);
        }
    }, []);

    const loadTeamMembers = useCallback(async () => {
        setLoadingTeamMembers(true);
        setTeamMembersError("");
        try {
            const data = await getTeamMembers();
            setTeamMembers(Array.isArray(data) ? data : []);
        } catch (error) {
            setTeamMembers([]);
            setTeamMembersError(error?.message || "Failed to load team members");
        } finally {
            setLoadingTeamMembers(false);
        }
    }, []);

    const loadProblems = useCallback(async () => {
        setLoadingProblems(true);
        setProblemsError("");
        try {
            const data = await getProblems();
            setProblems(Array.isArray(data) ? data : []);
        } catch (error) {
            setProblems([]);
            setProblemsError(error?.message || "Failed to load problems");
        } finally {
            setLoadingProblems(false);
        }
    }, []);

    const loadChanges = useCallback(async () => {
        setLoadingChanges(true);
        setChangesError("");
        try {
            const data = await getChanges();
            setChanges(Array.isArray(data) ? data : []);
        } catch (error) {
            setChanges([]);
            setChangesError(error?.message || "Failed to load changes");
        } finally {
            setLoadingChanges(false);
        }
    }, []);

    const loadPortalRequests = useCallback(async () => {
        setLoadingPortalRequests(true);
        setPortalRequestsError("");
        try {
            const data = await getPortalRequests();
            setPortalRequests(Array.isArray(data) ? data : []);
        } catch (error) {
            setPortalRequests([]);
            setPortalRequestsError(error?.message || "Failed to load portal requests");
        } finally {
            setLoadingPortalRequests(false);
        }
    }, []);

    useEffect(() => {
        loadIncidents();
        loadClients();
        loadTeamMembers();
        loadProblems();
        loadChanges();
        loadPortalRequests();
    }, [loadIncidents, loadClients, loadTeamMembers, loadProblems, loadChanges, loadPortalRequests]);

    function updatePortalRequestInState(updatedRequest) {
        if (!updatedRequest?.id) {
            return;
        }
        setPortalRequests((current) => current.map((request) => Number(request?.id) === Number(updatedRequest.id) ? { ...request, ...updatedRequest } : request));
        setSelectedPortalRequestPreview((current) => Number(current?.id) === Number(updatedRequest.id) ? { ...current, ...updatedRequest } : current);
    }

    async function handleConvertPortalRequest(portalRequestId) {
        const payload = await convertPortalRequest(portalRequestId);
        updatePortalRequestInState(payload?.portal_request);
        if (payload?.incident?.id) {
            setIncidents((current) => current.some((incident) => Number(incident?.id) === Number(payload.incident.id))
                ? current.map((incident) => Number(incident?.id) === Number(payload.incident.id) ? { ...incident, ...payload.incident } : incident)
                : [payload.incident, ...current]);
        }
        return payload;
    }

    async function handlePortalRequestStatusUpdate(portalRequestId, status) {
        const updatedRequest = await updatePortalRequestStatus(portalRequestId, status);
        updatePortalRequestInState(updatedRequest);
        return updatedRequest;
    }

    function handlePortalRequestSelect(portalRequest) {
        setSelectedPortalRequestId(portalRequest?.id || null);
        setSelectedPortalRequestPreview(portalRequest || null);
        setActivePage("PortalRequestDetail");
    }

    function handleBackFromPortalRequest() {
        setSelectedPortalRequestId(null);
        setSelectedPortalRequestPreview(null);
        setActivePage("Portal Requests");
    }

    function handleOpenPortalIncident(incident) {
        if (!incident?.id) {
            return;
        }
        setSelectedPortalRequestId(null);
        setSelectedPortalRequestPreview(null);
        setIncidentDetailReturnPage("Portal Requests");
        setSelectedIncidentId(incident.id);
        setSelectedIncidentPreview(incident);
        setActivePage("IncidentDetail");
    }

    function handleOpenPortalProblem(problem) {
        if (!problem?.id) {
            return;
        }
        setSelectedPortalRequestId(null);
        setSelectedPortalRequestPreview(null);
        setProblemDetailReturnPage("Portal Requests");
        setSelectedProblemId(problem.id);
        setSelectedProblemPreview(problem);
        setActivePage("ProblemDetail");
    }

    function handleOpenPortalChange(change) {
        if (!change?.id) {
            return;
        }
        setSelectedPortalRequestId(null);
        setSelectedPortalRequestPreview(null);
        setChangeDetailReturnPage("Portal Requests");
        setSelectedChangeId(change.id);
        setSelectedChangePreview(change);
        setActivePage("ChangeDetail");
    }

    async function handleIncidentUpdate(incidentID, updates) {
        if (!incidentID) {
            throw new Error("Incident id is required");
        }

        const updated = await updateIncident(incidentID, updates);

        setIncidents((prev) =>
            prev.map((incident) => (Number(incident?.id) === Number(incidentID) ? { ...incident, ...updated } : incident))
        );

        setSelectedIncidentPreview((prev) =>
            Number(prev?.id) === Number(incidentID) ? { ...prev, ...updated } : prev
        );

        return updated;
    }

    async function handleAssignIncident(rawIncident, assignedToID) {
        const incidentID = rawIncident?.id;
        if (!incidentID || !assignedToID) {
            return;
        }

        setAssigningByIncidentId((prev) => ({
            ...prev,
            [incidentID]: true,
        }));

        try {
            await handleIncidentUpdate(incidentID, { assigned_to_id: assignedToID });
            setIncidentsError("");
        } catch (error) {
            setIncidentsError(error?.message || "Failed to update incident assignee");
        } finally {
            setAssigningByIncidentId((prev) => {
                const next = { ...prev };
                delete next[incidentID];
                return next;
            });
        }
    }

    async function handleProblemUpdate(problemID, updates) {
        if (!problemID) {
            throw new Error("Problem id is required");
        }

        const updated = await updateProblem(problemID, updates);

        setProblems((prev) =>
            prev.map((problem) => (Number(problem?.id) === Number(problemID) ? { ...problem, ...updated } : problem))
        );

        setSelectedProblemPreview((prev) =>
            Number(prev?.id) === Number(problemID) ? { ...prev, ...updated } : prev
        );

        return updated;
    }

    async function handleChangeUpdate(changeID, updates) {
        if (!changeID) {
            throw new Error("Change id is required");
        }

        const updated = await updateChange(changeID, updates);

        setChanges((prev) =>
            prev.map((change) => (Number(change?.id) === Number(changeID) ? { ...change, ...updated } : change))
        );

        setSelectedChangePreview((prev) =>
            Number(prev?.id) === Number(changeID) ? { ...prev, ...updated } : prev
        );

        return updated;
    }

    function handleIncidentSelect(incident) {
        setIncidentDetailReturnPage(activePage);
        setSelectedIncidentId(incident?.id || null);
        setSelectedIncidentPreview(incident || null);
        setActivePage("IncidentDetail");
    }

    function handleBackFromIncident() {
        setSelectedIncidentId(null);
        setSelectedIncidentPreview(null);
        setActivePage(incidentDetailReturnPage || "Incidents");
        setIsAssignProblemModalOpen(false);
    }

    function handleOpenAssignProblemModal(incident) {
        setSelectedIncidentId(incident?.id || null);
        setSelectedIncidentPreview(incident || null);
        setIncidentDetailReturnPage(activePage);
        setActivePage("IncidentDetail");
        setIsAssignProblemModalOpen(true);
    }

    function handleCloseAssignProblemModal() {
        setIsAssignProblemModalOpen(false);
    }

    function handleProblemSelect(problem) {
        setProblemDetailReturnPage(activePage);
        setSelectedProblemId(problem?.id || null);
        setSelectedProblemPreview(problem || null);
        setActivePage("ProblemDetail");
    }

    function handleBackFromProblem() {
        setSelectedProblemId(null);
        setSelectedProblemPreview(null);
        setActivePage(problemDetailReturnPage || "Problems");
        setIsLinkChangeModalOpen(false);
        setIsLinkIncidentModalOpen(false);
    }

    function handleOpenLinkChangeModal(problem) {
        setSelectedProblemId(problem?.id || null);
        setSelectedProblemPreview(problem || null);
        setProblemDetailReturnPage(activePage);
        setActivePage("ProblemDetail");
        setIsLinkChangeModalOpen(true);
    }

    function handleCloseLinkChangeModal() {
        setIsLinkChangeModalOpen(false);
    }

    function handleOpenLinkIncidentModal(problem) {
        setSelectedProblemId(problem?.id || null);
        setSelectedProblemPreview(problem || null);
        setProblemDetailReturnPage(activePage);
        setActivePage("ProblemDetail");
        setIsLinkIncidentModalOpen(true);
    }

    function handleCloseLinkIncidentModal() {
        setIsLinkIncidentModalOpen(false);
    }

    function handleOpenLinkProblemModal(change) {
        setSelectedChangeId(change?.id || null);
        setSelectedChangePreview(change || null);
        setChangeDetailReturnPage(activePage);
        setActivePage("ChangeDetail");
        setIsLinkProblemModalOpen(true);
    }

    function handleCloseLinkProblemModal() {
        setIsLinkProblemModalOpen(false);
    }

    function findKnownTicket(candidate, suppliedTickets = []) {
        const supplied = suppliedTickets.find(
            (entry) => entry?.type === candidate.type && Number(entry?.ticket?.id) === Number(candidate.id)
        )?.ticket;
        if (supplied) {
            return supplied;
        }

        const collection = candidate.type === "problem" ? problems : changes;
        return collection.find((ticket) => Number(ticket?.id) === Number(candidate.id)) || { id: candidate.id };
    }

    async function handleUnlinkRelation({ relationId, tickets = [] }) {
        const payload = await deleteTicketRelation(relationId);
        const nextOrphans = (Array.isArray(payload?.orphaned_tickets) ? payload.orphaned_tickets : []).map((candidate) => ({
            ...candidate,
            ticket: findKnownTicket(candidate, tickets),
        }));

        if (nextOrphans.length > 0) {
            setOrphanResolutionError("");
            setOrphanedTickets((current) => [...current, ...nextOrphans]);
        }

        return payload;
    }

    function handleDismissOrphan() {
        setOrphanResolutionError("");
        setOrphanedTickets((current) => current.slice(1));
    }

    function handleLinkAnotherForOrphan() {
        const orphan = orphanedTickets[0];
        if (!orphan) {
            return;
        }

        handleDismissOrphan();
        if (orphan.type === "problem") {
            handleOpenLinkIncidentModal(orphan.ticket);
            return;
        }
        handleOpenLinkProblemModal(orphan.ticket);
    }

    async function handleDeleteOrphan() {
        const orphan = orphanedTickets[0];
        if (!orphan) {
            return;
        }

        setResolvingOrphan(true);
        setOrphanResolutionError("");
        try {
            if (orphan.type === "problem") {
                await deleteProblem(orphan.id);
                setProblems((current) => current.filter((problem) => Number(problem?.id) !== Number(orphan.id)));
                if (Number(selectedProblemId) === Number(orphan.id)) {
                    handleBackFromProblem();
                }
            } else {
                await deleteChange(orphan.id);
                setChanges((current) => current.filter((change) => Number(change?.id) !== Number(orphan.id)));
                if (Number(selectedChangeId) === Number(orphan.id)) {
                    handleBackFromChange();
                }
            }
            setOrphanedTickets((current) => current.slice(1));
        } catch (error) {
            setOrphanResolutionError(error?.message || `Failed to delete ${orphan.type}`);
        } finally {
            setResolvingOrphan(false);
        }
    }

    function handleChangeSelect(change) {
        setChangeDetailReturnPage(activePage);
        setSelectedChangeId(change?.id || null);
        setSelectedChangePreview(change || null);
        setActivePage("ChangeDetail");
    }

    function handleOpenTicket(ticketType, ticket) {
        if (!ticket?.id) {
            return;
        }

        setSelectedPortalRequestId(null);
        setSelectedPortalRequestPreview(null);
        setSelectedIncidentId(null);
        setSelectedIncidentPreview(null);
        setSelectedProblemId(null);
        setSelectedProblemPreview(null);
        setSelectedChangeId(null);
        setSelectedChangePreview(null);

        switch (ticketType) {
            case "incident":
                setIncidentDetailReturnPage("Incidents");
                setSelectedIncidentId(ticket.id);
                setSelectedIncidentPreview(ticket);
                setActivePage("IncidentDetail");
                return;
            case "problem":
                setProblemDetailReturnPage("Problems");
                setSelectedProblemId(ticket.id);
                setSelectedProblemPreview(ticket);
                setActivePage("ProblemDetail");
                return;
            case "change":
                setChangeDetailReturnPage("Changes");
                setSelectedChangeId(ticket.id);
                setSelectedChangePreview(ticket);
                setActivePage("ChangeDetail");
                return;
            default:
                return;
        }
    }

    function handleBackFromChange() {
        setSelectedChangeId(null);
        setSelectedChangePreview(null);
        setActivePage(changeDetailReturnPage || "Changes");
        setIsLinkProblemModalOpen(false);
    }

    useEffect(() => {
        if (selectedPortalRequestId) {
            setPageHeader({
                title: selectedPortalRequest?.subject ? `Request: ${selectedPortalRequest.subject}` : "Portal Request",
                back: true,
                onBack: handleBackFromPortalRequest,
                children: null,
            });
            return;
        }

        if (selectedIncidentId) {
            const incidentTitle = selectedIncident?.number ? `Incident: ${selectedIncident.number}` : "Incident";
            setPageHeader({
                title: incidentTitle,
                back: true,
                onBack: handleBackFromIncident,
                children: null,
            });
            return;
        }

        if (selectedProblemId) {
            const problemTitle = selectedProblem?.number ? `Problem: ${selectedProblem.number}` : "Problem";
            setPageHeader({
                title: problemTitle,
                back: true,
                onBack: handleBackFromProblem,
                children: null,
            });
            return;
        }

        if (selectedChangeId) {
            const changeTitle = selectedChange?.number ? `Change: ${selectedChange.number}` : "Change";
            setPageHeader({
                title: changeTitle,
                back: true,
                onBack: handleBackFromChange,
                children: null,
            });
            return;
        }

        setPageHeader({
            title: IT_PAGE_HEADER_TITLES[activePage] || IT_PAGE_HEADER_TITLES.Dashboard,
            back: false,
            onBack: null,
            children: null,
        });
    }, [
        activePage,
        selectedIncidentId,
        selectedIncident,
        selectedProblemId,
        selectedProblem,
        selectedChangeId,
        selectedChange,
        selectedPortalRequestId,
        selectedPortalRequest,
    ]);

    function renderPage() {
        if (selectedPortalRequestId) {
            return (
                <PortalRequestDetail
                    initialRequest={selectedPortalRequest}
                    clients={clients}
                    onConvert={handleConvertPortalRequest}
                    onUpdateStatus={handlePortalRequestStatusUpdate}
                    onOpenIncident={handleOpenPortalIncident}
                    onOpenProblem={handleOpenPortalProblem}
                    onOpenChange={handleOpenPortalChange}
                />
            );
        }

        if (selectedIncidentId) {
            return (
                <IncidentDetail
                    incidentId={selectedIncidentId}
                    initialIncident={selectedIncident}
                    clients={clients}
                    teamMembers={teamMembers}
                    products={products}
                    onBack={handleBackFromIncident}
                    onIncidentUpdate={handleIncidentUpdate}
                    onIncidentRefresh={loadIncidents}
                    onAssignProblem={handleOpenAssignProblemModal}
                    onUnlinkRelation={handleUnlinkRelation}
					onOpenTicket={handleOpenTicket}
                    relationRefreshVersion={relationRefreshVersion}
                />
            );
        }

        if (selectedProblemId) {
            return (
                <ProblemDetail
                    problemId={selectedProblemId}
                    initialProblem={selectedProblem}
                    teamMembers={teamMembers}
                    onBack={handleBackFromProblem}
                    onProblemUpdate={handleProblemUpdate}
                    onProblemRefresh={loadProblems}
                    onLinkChange={handleOpenLinkChangeModal}
                    onLinkIncident={handleOpenLinkIncidentModal}
                    onUnlinkRelation={handleUnlinkRelation}
					onOpenTicket={handleOpenTicket}
                    relationRefreshVersion={relationRefreshVersion}
                />
            );
        }

        if (selectedChangeId) {
            return (
                <ChangeDetail
                    changeId={selectedChangeId}
                    initialChange={selectedChange}
                    teamMembers={teamMembers}
                    onBack={handleBackFromChange}
                    onChangeUpdate={handleChangeUpdate}
                    onLinkProblem={handleOpenLinkProblemModal}
                    onUnlinkRelation={handleUnlinkRelation}
					onOpenTicket={handleOpenTicket}
                    relationRefreshVersion={relationRefreshVersion}
                />
            );
        }

        switch (activePage) {
            case "Portal Requests":
                return (
                    <PortalRequests
                        portalRequests={portalRequests}
                        clients={clients}
                        loading={loadingPortalRequests}
                        error={portalRequestsError}
                        onSelect={handlePortalRequestSelect}
                    />
                );
            case "Incidents":
                return (
                    <Incidents
                        incidents={incidents}
                        clients={clients}
                        teamMembers={teamMembers}
                        products={products}
                        loadingIncidents={loadingIncidents}
                        incidentsError={incidentsError}
                        loadingClients={loadingClients}
                        clientsError={clientsError}
                        loadingTeamMembers={loadingTeamMembers}
                        teamMembersError={teamMembersError}
                        onIncidentsRefresh={loadIncidents}
                        onClientsRefresh={loadClients}
                        onTeamMembersRefresh={loadTeamMembers}
                        onIncidentAssign={handleAssignIncident}
                        assigningIncidentIds={assigningByIncidentId}
                        onIncidentSelect={handleIncidentSelect}
                    />
                );
            case "Problems":
                return (
                    <Problems
                        problems={problems}
                        teamMembers={teamMembers}
                        loadingProblems={loadingProblems}
                        problemsError={problemsError}
                        loadingTeamMembers={loadingTeamMembers}
                        teamMembersError={teamMembersError}
                        onProblemsRefresh={loadProblems}
                        onTeamMembersRefresh={loadTeamMembers}
                        onProblemSelect={handleProblemSelect}
                    />
                );
            case "Changes":
                return (
                    <Changes
                        changes={changes}
                        teamMembers={teamMembers}
                        loadingChanges={loadingChanges}
                        changesError={changesError}
                        loadingTeamMembers={loadingTeamMembers}
                        teamMembersError={teamMembersError}
                        onChangesRefresh={loadChanges}
                        onTeamMembersRefresh={loadTeamMembers}
                        onChangeSelect={handleChangeSelect}
                    />
                );
            case "Dashboard":
            default:
                return (
                    <Dashboard
                        incidents={incidents}
                        problems={problems}
                        changes={changes}
                        portalRequests={portalRequests}
                        loading={loadingIncidents || loadingProblems || loadingChanges || loadingPortalRequests}
						incidentsError={incidentsError}
						problemsError={problemsError}
						changesError={changesError}
						portalRequestsError={portalRequestsError}
                        onOpenIncidents={() => setActivePage("Incidents")}
                        onOpenPortalRequests={() => setActivePage("Portal Requests")}
                        onOpenIncident={handleIncidentSelect}
                        onOpenPortalRequest={handlePortalRequestSelect}
                    />
                );
        }
    }

    return (
        <div className="crm-layout app">
            <div className="nav-section">
                <NavBar
                    activePage={activePage}
                    navItems={itNavItems}
                    onPageChange={(page) => {
                        setSelectedPortalRequestId(null);
                        setSelectedPortalRequestPreview(null);
                        setSelectedIncidentId(null);
                        setSelectedIncidentPreview(null);
                        setIncidentDetailReturnPage(page);
                        setSelectedProblemId(null);
                        setSelectedProblemPreview(null);
                        setProblemDetailReturnPage(page);
                        setSelectedChangeId(null);
                        setSelectedChangePreview(null);
                        setChangeDetailReturnPage(page);
                        setIsLinkChangeModalOpen(false);
                        setIsLinkIncidentModalOpen(false);
                        setIsLinkProblemModalOpen(false);
                        setActivePage(page);
                    }}
                    footerAction={{
                        label: "Home",
                        icon: <GoHome size={24} />,
                        onClick: () => {
                            window.location.href = "/";
                        },
                    }}
                />
            </div>
            <div className="page-logo-container">
                <img className="nav-logo" src={TicketsLogo} alt="Tickets Logo" />
            </div>
            <PageHeaderProvider userMenu={(
                <div className="it-header-actions">
                    <HeaderClockAction />
                    <AppSwitcher apps={appLinks} activeLabel="Home" />
                    <div className="it-header-user">
                        <AppUserMenu name={profileName} id={profileId} />
                    </div>
                </div>
            )}
            >
                <PageHeader className="it-page-header" title={pageHeader.title} back={pageHeader.back} onBack={pageHeader.onBack}>
                    {pageHeader.children}
                </PageHeader>
                {renderPage()}
            </PageHeaderProvider>
            <AssignProblemModal
                isOpen={isAssignProblemModalOpen}
                onClose={handleCloseAssignProblemModal}
                incident={selectedIncident}
                teamMembers={teamMembers}
                onCompleted={async ({ problem, problemId, openProblem }) => {
                    await loadIncidents();
                    await loadProblems();
                    setRelationRefreshVersion((current) => current + 1);

                    if (!openProblem) {
                        return;
                    }

                    const nextProblem = problem || (problemId ? { id: problemId } : null);
                    if (!nextProblem?.id) {
                        return;
                    }

                    setSelectedIncidentId(null);
                    setSelectedIncidentPreview(null);
                    setIsAssignProblemModalOpen(false);

                    setProblemDetailReturnPage("Incidents");
                    setSelectedProblemId(nextProblem.id);
                    setSelectedProblemPreview(nextProblem);
                    setActivePage("ProblemDetail");
                }}
            />
            <LinkChangeModal
                isOpen={isLinkChangeModalOpen}
                onClose={handleCloseLinkChangeModal}
                problem={selectedProblem}
                teamMembers={teamMembers}
                onCompleted={async ({ problem: updatedProblem }) => {
                    if (updatedProblem?.id) {
                        setProblems((prev) =>
                            prev.map((problem) =>
                                Number(problem?.id) === Number(updatedProblem.id)
                                    ? { ...problem, ...updatedProblem }
                                    : problem
                            )
                        );
                        setSelectedProblemPreview((prev) =>
                            Number(prev?.id) === Number(updatedProblem.id)
                                ? { ...prev, ...updatedProblem }
                                : prev
                        );
                    }
                    await loadProblems();
                    setRelationRefreshVersion((current) => current + 1);
                }}
            />
            <LinkIncidentModal
                isOpen={isLinkIncidentModalOpen}
                onClose={handleCloseLinkIncidentModal}
                problem={selectedProblem}
                onCompleted={async () => {
                    await loadIncidents();
                    await loadProblems();
                    setRelationRefreshVersion((current) => current + 1);
                }}
            />
            <LinkProblemModal
                isOpen={isLinkProblemModalOpen}
                onClose={handleCloseLinkProblemModal}
                change={selectedChange}
                onCompleted={async () => {
                    await loadChanges();
                    await loadProblems();
                    setRelationRefreshVersion((current) => current + 1);
                }}
            />
            <OrphanedTicketModal
                orphan={orphanedTickets[0] || null}
                isOpen={orphanedTickets.length > 0 && !isLinkIncidentModalOpen && !isLinkProblemModalOpen}
                saving={resolvingOrphan}
                error={orphanResolutionError}
                onDelete={handleDeleteOrphan}
                onLinkAnother={handleLinkAnotherForOrphan}
                onClose={handleDismissOrphan}
            />
        </div>

    )
}

export default IT_Main

